import React, { useEffect, useMemo, useState } from 'react';
import { Box, Stack, TableCell, TableRow, Typography, useTheme } from '@mui/material';
import { ITableHeadings, ITableMetadata } from '../../../common/interfaces/common.interface';
import BasicTable from '../../../components/BasicTable/BasicTable';
import { initialMetadataState, MESSAGE_TYPE } from '../../../common/constants/common.constants';
import TableHeader from '../../../components/TableHeader';
import { ReactComponent as AddIcon } from '../../../assets/add-icon.svg';
import { createNotification } from '../../../utilities/helper/notification.helper';
import GenericButton, { GenericButtonTypeEnum } from '../../../components/GenericButton';
import { GeneralStatusEnum, SortOrderEnum } from '../../../common/enums/common.enum';
import GenericInfoModal from '../../../components/GenericInfoModal';
import { format } from 'date-fns';
import { createChildMemberships, deactivateChildMembership, getChildrenMemberships } from '../../../api/group.api';
import { ChildMembershipDto, ChildWithoutMembership } from '../../../common/interfaces/group.interface';
import { GroupChildrenTabSortKeyEnum } from '../../../common/enums/group.enum';
import CreateChildMembershipModal from './CreateChildMembershipModal';
import ToggleWithLabel from '../../../components/ToggleWithLabel';
import { ReactComponent as DeactivateIcon } from '../../../assets/deactivate-icon.svg';
import { formatDateWithDots } from '../../../utilities/helper/date.helper';

export const CHILDREN_TABLE_HEADINGS: ITableHeadings[] = [
  { id: 1, value: '', label: 'Name', sortable: true, sortKey: GroupChildrenTabSortKeyEnum.NAME, width: '20%' },
  { id: 2, value: '', label: 'Parents', sortable: true, sortKey: GroupChildrenTabSortKeyEnum.PARENTS, width: '30%' },
  { id: 3, value: '', label: 'Membership', sortable: true, sortKey: GroupChildrenTabSortKeyEnum.MEMBERSHIP },
  { id: 4, value: '', label: 'Actions', sortable: false, sortKey: undefined }
];

const GroupChildrenTab: React.FC<{ groupId: string; kindergartenId: string; isGroupActive: boolean }> = ({
  groupId,
  kindergartenId,
  isGroupActive
}) => {
  const [memberships, setMemberships] = useState<ChildMembershipDto[]>([]);
  const [loader, setLoader] = useState<boolean>(false);
  const [metadata, setMetadata] = useState<ITableMetadata>(initialMetadataState);

  const [searchParams, setSearchParams] = useState<string>('');
  const [withHistory, setWithHistory] = useState(false);

  const [sortBy, setSortBy] = useState<GroupChildrenTabSortKeyEnum>();
  const [sortOrder, setSortOrder] = useState<SortOrderEnum>();
  const [membershipToDeactivate, setMembershipToDeactivate] = useState<ChildMembershipDto | null>(null);

  const [isAddMembershipModalActive, setIsAddMembershipModalActive] = useState(false);

  const theme = useTheme();

  useEffect(() => {
    loadMemberships();
  }, [groupId, metadata.currentPage, metadata.itemsPerPage, searchParams, sortBy, sortOrder, withHistory]);

  const loadMemberships = async () => {
    setLoader(true);
    try {
      const result = await getChildrenMemberships(
        groupId,
        metadata.currentPage,
        metadata.itemsPerPage,
        sortBy,
        sortOrder,
        searchParams,
        withHistory
      );
      setMemberships(result.results ?? []);
      if (result.paginationInfo) {
        setMetadata(prev => ({
          ...prev,
          totalItems: result.paginationInfo?.totalItems ?? 0,
          totalPages: result.paginationInfo?.totalPages ?? 0
        }));
      }
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, 'An error occurred while fetching memberships');
    } finally {
      setLoader(false);
    }
  };

  const handleSearchChange = (newSearch: string) => {
    setMetadata(prev => ({ ...prev, currentPage: 1 }));
    setSearchParams(newSearch);
  };

  const handleSortChange = (sortKey: string | null, order: 'asc' | 'desc' | null) => {
    setSortBy(sortKey ? (sortKey as GroupChildrenTabSortKeyEnum) : undefined);
    setSortOrder(order ? (order as SortOrderEnum) : SortOrderEnum.DESC);
  };

  const handelDeactivateChildMembership = async (membershipId: string) => {
    if (!kindergartenId) return;
    try {
      const response = await deactivateChildMembership(groupId, membershipId);
      if (response.message) {
        createNotification(MESSAGE_TYPE.SUCCESS, response.message);
      }
      setMemberships(prev =>
        prev.map(t =>
          t.id === membershipId
            ? {
                ...t,
                status: GeneralStatusEnum.INACTIVE,
                dateTo: format(new Date(), 'yyyy-MM-dd')
              }
            : t
        )
      );
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, 'Failed to change status for child membership');
    }
  };

  const handleConfirmDeactivation = async () => {
    if (!membershipToDeactivate) return;
    await handelDeactivateChildMembership(membershipToDeactivate.id);
    setMembershipToDeactivate(null);
  };

  const handleCreateClick = async () => {
    setIsAddMembershipModalActive(true);
  };

  const handleCreateChildMembership = async (newChildMemberships: ChildWithoutMembership[]) => {
    try {
      const response = await createChildMemberships(
        groupId,
        kindergartenId,
        newChildMemberships.map(item => item.id)
      );
      await loadMemberships();
      setIsAddMembershipModalActive(false);
      if (response.message) {
        createNotification(MESSAGE_TYPE.SUCCESS, response.message);
      }
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, 'Failed to create child memberships');
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h2">Children</Typography>
        {isGroupActive && (
          <GenericButton
            buttonType={GenericButtonTypeEnum.Primary}
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateClick}
            label="Add"
          />
        )}
      </Stack>
      <Box
        sx={{
          border: '1px solid #F0F0F0',
          borderRadius: 4
        }}
      >
        <Box
          sx={{
            display: 'flex'
          }}
        >
          <TableHeader searchTitle="Search by name" sortOptions={[]} setSearchValue={handleSearchChange} />
          <ToggleWithLabel label="With history" checked={withHistory} onChange={setWithHistory} />
        </Box>

        <Box>
          <BasicTable
            headings={CHILDREN_TABLE_HEADINGS}
            loader={loader}
            metadata={metadata}
            setMetadata={setMetadata}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={handleSortChange}
          >
            {memberships.map(membership => (
              <TableRow
                key={membership.id}
                sx={{
                  background: membership.status === GeneralStatusEnum.INACTIVE ? '#ebe7e791;' : 'auto',
                  opacity: membership.status === GeneralStatusEnum.INACTIVE ? 0.6 : 'auto'
                }}
              >
                <TableCell>
                  {membership.firstName} {membership.lastName}
                </TableCell>
                <TableCell>
                  {membership.parents?.length
                    ? membership.parents.map(
                        (item, index) => `${item.fullName}${membership.parents && index !== membership.parents.length - 1 ? ', ' : ''}`
                      )
                    : 'Not set'}
                </TableCell>
                <TableCell>{`${formatDateWithDots(membership.dateFrom)} - ${
                  membership.dateTo ? formatDateWithDots(membership.dateTo) : 'present'
                }`}</TableCell>
                <TableCell>
                  {membership.status === GeneralStatusEnum.ACTIVE && isGroupActive && (
                    <GenericButton
                      buttonType={GenericButtonTypeEnum.Neutral}
                      variant="text"
                      sx={{
                        color: theme.palette.error.main
                      }}
                      startIcon={<DeactivateIcon />}
                      onClick={() => setMembershipToDeactivate(membership)}
                      label="Deactivate"
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
            {membershipToDeactivate && (
              <GenericInfoModal
                open={true}
                onClose={() => setMembershipToDeactivate(null)}
                onConfirm={handleConfirmDeactivation}
                label="Deactivate child membership"
                entityName={membershipToDeactivate.firstName + ' ' + membershipToDeactivate.lastName}
                confirmLabel="Deactivate"
                message="Are you sure you want to deactivate "
              />
            )}
          </BasicTable>
        </Box>
      </Box>

      {isAddMembershipModalActive && (
        <CreateChildMembershipModal
          onClose={() => setIsAddMembershipModalActive(false)}
          onConfirm={handleCreateChildMembership}
          kindergartenId={kindergartenId}
          groupId={groupId}
        />
      )}
    </Box>
  );
};
export default GroupChildrenTab;
